
import Vue from 'vue';
import ImageGallery from '@/components/ImageGallery/ImageGallery.vue';
import LibraryFilterPanel from '@/components/ImageGallery/LibraryFilterPanel.vue';
import LibrarySearchField from '@/components/ImageGallery/LibrarySearchField.vue';
import EmptyState from '@/components/states/EmptyState.vue';
import {
	AppliedLibraryFilters,
	countAppliedLibraryFilters,
	createEmptyAppliedLibraryFilters,
} from '@/utils/libraryFilterFacets';
import { mapActions, mapState } from 'vuex';
import RunnitDialog from '@/views/Runnits/base/RunnitDialog.vue';
import RunnitNavTabs from '@/views/Runnits/base/RunnitNavTabs.vue';
import BaseStyledMenu from '@/components/base/BaseStyledMenu.vue';
import GlassButton from '@/components/base/GlassButton.vue';
import { IMAGE_GALLERY_DIALOG_NAV } from '@/components/ImageGallery/utils';
import {
	SELECTED_COLUMN_MENU_ITEMS,
	SELECTED_COLUMNS_MAP,
	SELECTED_IMAGE_MODE_MENU_ITEMS,
	SELECTED_IMAGE_MODE,
} from '@/views/Runnits/constants';
import { RunnitsImageSelectMixin } from '@/mixins/RunnitsImageSelectMixin';
import SelectedColumnsButton from '@/components/ImageGallery/SelectedColumnsButton.vue';
import BaseButton from '@/components/base/BaseButton.vue';
import _cloneDeep from 'lodash/cloneDeep';

export default Vue.extend({
	name: 'ImageGalleryDialog',
	props: {
		elIdSuffix: { type: String, required: true },
		selectionModeTriggerId: { type: String },
		persistent: { type: Boolean, default: false },
		maxWidth: { type: [Number, String], default: '80%' },
		hideNav: { type: Boolean, default: false },
		navTab: { type: String, default: null },
	},
	mixins: [
		RunnitsImageSelectMixin,
	],
	data () {
		return {
			SELECTED_IMAGE_MODE,
			IMAGE_GALLERY_DIALOG_NAV,

			imageGalleryCurrNavTabId: null,
			imageGalleryNavTabs: [
				{
					id: IMAGE_GALLERY_DIALOG_NAV.GENERATIONS,
					label: 'Your Generations',
					icon: 'mdi-image-multiple-outline',
				},
				{
					id: IMAGE_GALLERY_DIALOG_NAV.UPLOADS,
					label: 'Uploads',
					icon: 'mdi-cloud-upload-outline',
				},
				{
					id: IMAGE_GALLERY_DIALOG_NAV.FAVORITES,
					label: 'Favorites',
					icon: 'mdi-heart-outline',
				},
				// { // By default this tab isn't used for the runnit workflows so we don't want them visible
				// 	id: IMAGE_GALLERY_DIALOG_NAV.AVATARS,
				// 	label: 'Avatars',
				// 	icon: 'mdi-cloud-upload-outline',
				// },
			],
			selectedImageModeMenuItems: SELECTED_IMAGE_MODE_MENU_ITEMS,
			selectColumnsMap: SELECTED_COLUMNS_MAP,
			selectedColumnsMenuItems: SELECTED_COLUMN_MENU_ITEMS,
			initialSelectionSnapshot: null as any,
			isConfirmed: false,
			filtersExpanded: false,
			// Committed facet filters that drive <image-gallery>. Cleared on tab change.
			appliedFilters: createEmptyAppliedLibraryFilters(),
		};
	},
	computed: {
		...mapState([
			'runnitState',
		]),
		currentSelectionTriggerId (): string {
			return this.currentSelectionState ? this.currentSelectionState.triggeringId : '';
		},
		// Filter is offered on Generations only. Favorites bypasses facet mode
		// (decision 7), so the toolbar is hidden there to avoid dead controls,
		// matching showFacetToolbar on RunnitLibraryPage.
		showFacetToolbar (): boolean {
			return this.imageGalleryCurrNavTabId === IMAGE_GALLERY_DIALOG_NAV.GENERATIONS;
		},
		hasActiveFilters (): boolean {
			// Only the panel's narrowing facets count — NOT scope ('personal' etc.,
			// always a non-empty enum), teamIds ([], truthy) or the search term, which
			// has its own box. A blind Object.values(...).some lit this up on a
			// pristine library. The roster lives in LIBRARY_FILTER_FIELDS, so this
			// can't drift from RunnitLibraryPage.
			return countAppliedLibraryFilters(this.appliedFilters) > 0;
		},
		isMultiImageConfirmMode (): boolean {
			return !!(this.currentSelectionTriggerId && this.currentSelectionTriggerId.startsWith('multi-image-input-'));
		},
		isBatchMediaConfirmMode (): boolean {
			return !!(this.currentSelectionTriggerId && this.currentSelectionTriggerId.startsWith('batch-gen-media-'));
		},
		showConfirmCancelButtons (): boolean {
			return !!(
				this.currentSelectionTriggerId &&
				(
					this.isMultiImageConfirmMode ||
					this.isBatchMediaConfirmMode
				)
			);
		},
		selectionLimit (): number | null {
			if (!this.isMultiImageConfirmMode) return null;
			return this.currentSelectionState?.fieldAwaitingImageGallerySelection?.display?.maxImages || null;
		},
		currentSelectionCount (): number {
			const uploads = this.currentSelectionState?.selectedUploads?.length || 0;
			const results = this.currentSelectionState?.selectedNodeRunResults?.length || 0;
			return uploads + results;
		},
		isSelectionLimitExceeded (): boolean {
			if (!this.selectionLimit) return false;
			return this.currentSelectionCount > this.selectionLimit;
		},
	},
	watch: {
		navTab: {
			immediate: true,
			handler (newVal, oldVal) {
				if (newVal !== oldVal) {
					this.imageGalleryCurrNavTabId = newVal;
				}
			}
		},
		// Changing tabs clears and resets the filters (spec).
		imageGalleryCurrNavTabId () {
			this.resetFilters();
		},
		'runnitState.imageGalleryDialogOpen': {
			handler (val) {
				if (val) {
					this.initialSelectionSnapshot = null;
					if (this.currentSelectionState && this.currentSelectionState.sortOrder > 0) {
						this.initialSelectionSnapshot = _cloneDeep(this.currentSelectionState);
					}
				} else {
					this.initialSelectionSnapshot = null;
				}
			}
		},
	},
	methods: {
		...mapActions([
			'updateRunnitState',
		]),
		onOpenInput (open: boolean) {
			if (open !== this.runnitState.imageGalleryDialogOpen) {
				this.updateRunnitState({
					imageGalleryDialogOpen: !!open,
				});
			}
			// Exit selection mode when dialog closes (whether value changed or not)
			if (!open) {
				// In multi-select flows, closing without confirm should discard in-dialog changes.
				if (this.showConfirmCancelButtons && !this.isConfirmed) {
					this.revertSelection();
				}
				this.onAbandonImageSelection();
				this.isConfirmed = false; // Reset flag
			}
		},
		onCancel () {
			this.onOpenInput(false);
		},
		revertSelection () {
			if (this.initialSelectionSnapshot) {
				this.updateSelectionState(this.initialSelectionSnapshot);
			}
		},
		onRevert () {
			this.revertSelection();
			this.onCancel();
		},
		onConfirm () {
			this.isConfirmed = true;
			this.onCancel();
		},
		onCloseClick () {
			this.onCancel();
		},
		onSelectionChosen () {
			if (!this.showConfirmCancelButtons) {
				this.onCancel();
			}
		},
		onFiltersApply (applied: AppliedLibraryFilters) {
			// The panel builds this from its own draft, which never sees the search
			// box, so keep the box's term rather than let Apply or Clear all wipe it.
			this.appliedFilters = { ...applied, search: this.appliedFilters.search };
		},
		onSearch (search: string) {
			this.appliedFilters = { ...this.appliedFilters, search };
		},
		// Clear committed + draft filters and the search term, and collapse the
		// panel (tab change).
		resetFilters () {
			this.appliedFilters = createEmptyAppliedLibraryFilters();
			this.filtersExpanded = false;
			const panel: any = this.$refs.filterPanel;
			if (panel && typeof panel.reset === 'function') {
				panel.reset();
			}
		},
		onAbandonImageSelection () {
			// Exit selection mode using the current active selection state's triggering ID
			// This ensures we exit the correct selection mode regardless of which component opened it
			if (this.currentSelectionState && this.currentSelectionState.triggeringId) {
				this.exitSelectionMode(this.currentSelectionState.triggeringId);
			} else if (this.selectionModeTriggerId) {
				// Fallback to prop if no active selection state (for backwards compatibility)
				this.exitSelectionMode(this.selectionModeTriggerId);
			}
		},
	},
	components: {
		ImageGallery,
		LibraryFilterPanel,
		LibrarySearchField,
		EmptyState,
		RunnitDialog,
		RunnitNavTabs,
		BaseStyledMenu,
		GlassButton,
		SelectedColumnsButton,
		BaseButton,
	},
});
